/**
 * Format a price (0-1) as cents for display.
 *
 * Default precision is 1 decimal — fits compact grids (EventCard,
 * TopHoldersTable, PinnedBoxes) where "17¢" / "17.5¢" is enough.
 *
 * Pass `decimals=2` for surfaces where the user must see the EXACT
 * purchase / fill price down to a hundredth of a cent: Positions table,
 * Activity feed, trade toasts, copy-trade notifications. The keypi
 * 2026-05-27 incident — avg "1¢" vs now "1¢" while showing -50% P/L —
 * was caused by `(price * 100).toFixed(0)` collapsing $0.005 and
 * $0.010 to the same "1¢" string. With decimals=1 you'd see "1¢ vs
 * 0.5¢"; with decimals=2 you'd see "1¢ vs 0.5¢" too — both fine for
 * that case — but a leader fill at $0.0198 needs decimals=2 to render
 * as "1.98¢" instead of being rounded to "2¢".
 *
 * Trailing zeros are trimmed: 1.50 → "1.5¢", 1.00 → "1¢".
 *
 * Uses Math.round to avoid float artifacts (0.33 * 100 = 33.00000001).
 */
export function formatCents(price: number, decimals: 1 | 2 = 1): string {
  const cents = price * 100;
  const factor = decimals === 2 ? 100 : 10;
  const rounded = Math.round(cents * factor) / factor;
  if (Number.isInteger(rounded)) {
    return `${Math.round(cents)}¢`;
  }
  // parseFloat strips trailing zeros from the toFixed output so "1.50"
  // renders as "1.5¢" and "1.20¢" never appears.
  return `${parseFloat(rounded.toFixed(decimals))}¢`;
}
